import type { operations } from './schema';

/**
 * Cliente da API. Os tipos vêm do `openapi.json` gerado pelo backend a partir
 * dos mesmos schemas Zod que validam as requisições (guardrail G-CONTRACT):
 * se uma rota mudar sem o contrato ser regenerado, o CI quebra.
 *
 * Nunca editar `schema.ts` à mão — é gerado por `pnpm api:types`.
 */

/**
 * O canal por onde a pessoa chegou, tirado do próprio contrato.
 *
 * **Derivado, e não importado do `@titularis/analytics`.** Quem produz esta
 * forma é o pacote de analytics; quem a transporta é este cliente. Se ele
 * importasse de lá, o contrato passaria a depender de um pacote de navegador
 * para descrever um corpo de requisição - e o dia em que as duas formas
 * divergissem, a divergência não apareceria em lugar nenhum. Derivando do
 * `schema.ts`, quem manda é o backend, que é quem grava.
 */
export type Origem = NonNullable<
  NonNullable<
    operations['postBillingCheckout']['requestBody']
  >['content']['application/json']['atribuicao']
>;

/**
 * O que se pode pedir para ser avisado, e de onde o pedido partiu.
 *
 * Derivados do contrato, como a `Origem` acima: quem manda na lista é o Zod da
 * rota, e um literal repetido aqui divergiria no dia em que um tópico entrasse.
 */
export type TopicoDeLista = NonNullable<
  NonNullable<operations['postLeads']['requestBody']>['content']['application/json']['topic']
>;
export type OrigemDeLista = NonNullable<
  NonNullable<operations['postLeads']['requestBody']>['content']['application/json']['source']
>;

/** Uma linha do funil por canal, como o painel de Origem a lê. */
export type LinhaDoFunil =
  operations['getAttributionFunnel']['responses']['200']['content']['application/json'][number];


/** Resposta de uma operação, extraída do contrato. */
type JsonResponse<TOperation extends keyof operations, TStatus extends number> =
  operations[TOperation] extends { responses: infer TResponses }
    ? TStatus extends keyof TResponses
      ? TResponses[TStatus] extends { content: { 'application/json': infer TBody } }
        ? TBody
        : never
      : never
    : never;

/**
 * Parâmetros de consulta de uma operação, extraídos do contrato.
 *
 * É o que faz vocabulário fechado - `recorte`, `ordem`, `estado` - chegar à tela
 * pelo contrato, e não copiado à mão: a lista escrita duas vezes diverge na
 * primeira opção nova, e a divergência aparece como filtro que a API recusa
 * (G-CONTRACT).
 */
type JsonQuery<TOperation extends keyof operations> =
  operations[TOperation] extends { parameters: { query?: infer TQuery } } ? TQuery : never;

/** Corpo de requisição de uma operação, extraído do contrato. */
type JsonBody<TOperation extends keyof operations> =
  operations[TOperation] extends { requestBody?: { content: { 'application/json': infer TBody } } }
    ? TBody
    : never;

export type HealthResponse = JsonResponse<'getHealth', 200>;

export type Exam = JsonResponse<'getExamsId', 200>;
export type ExamInput = JsonBody<'postExams'>;
export type ExamList = JsonResponse<'getExams', 200>;
export type ExamModule = ExamInput['module'];
export type ExamStatus = Exam['status'];
export type ItemSimples = JsonResponse<'postBoards', 200>;
export type CorpusImportReport = JsonResponse<'postCorpusLegislations', 200>;
export type CorpusIngestReport = JsonResponse<'postCorpusDocuments', 200>;
export type CorpusHit = JsonResponse<'getCorpusSearch', 200>[number];
export type FonteDeLegislacao = JsonResponse<'getCorpusLegislations', 200>[number];
/** O que a reimportação devolve: o diff e as questões que precisam de revisão. */
export interface RelatorioDeReimportacao {
  citation: string;
  encontrados: number;
  redacoesAnteriores: number;
  anteriores: number;
  novos: string[];
  alterados: string[];
  sumidos: string[];
  colisoes: string[];
  questoesAfetadas: { questionId: string; position: number | null; enderecos: string[] }[];
}
/** A rota devolve o documento cru; o tipo sai do que a tela usa. */
export interface CorpusDocument {
  id: string;
  title: string;
  source: string;
  kind: string;
  tier: 'registro' | 'texto';
  fileName: string;
  recordCount: number;
  report: CorpusIngestReport | null;
  outdatedAt: string | null;
  outdatedReason: string | null;
  importedAt: string | null;
  importedBy: string | null;
}

/**
 * Uma falta do acervo, como o backlog a mostra (D-39).
 *
 * `address` nulo é a **fonte inteira** e não um dispositivo dela: a Lei
 * 9.514/1997 que não está no acervo se resolve importando a lei, enquanto um
 * parágrafo que falta se resolve reimportando a que já está lá.
 */
export type LacunaDoAcervo = JsonResponse<'getCorpusGaps', 200>[number];
export type FechamentoDeLacuna = JsonResponse<'postCorpusGapsIdResolve', 200>;

/**
 * Legislação Comentada por artigo (F14).
 *
 * A lei sai com o texto oficial sempre e o comentário conforme o direito: o
 * `comentario` vem nulo para quem não pode lê-lo, e o `temComentario` continua
 * verdadeiro - é o par que o paywall usa para dizer que há algo ali (D-42).
 */
export type LeiDoCatalogo = JsonResponse<'getLaws', 200>[number];
/** A prateleira de uma lei no catálogo. Vem fechada pelo contrato da API. */
export type AreaDaLei = LeiDoCatalogo['areas'][number];
/**
 * As estantes do acervo, **na ordem em que a tela as empilha**.
 *
 * A ordem é por peso no concurso, e não alfabética: quem abre a Legislação
 * Comentada de Titular de Cartório vem pelo notarial e registral, e uma estante
 * de "Civil" antes dela poria o principal no meio da página.
 *
 * O nome mora aqui, e não na tela, porque as duas telas que o mostram - o
 * catálogo do aluno e a classificação no admin - têm de chamar a estante pelo
 * mesmo nome. O `slug` é o que o contrato fechou; isto é como se lê.
 */
export const AREAS_DA_LEI: readonly { area: AreaDaLei; nome: string }[] = [
  { area: 'notarial-e-registral', nome: 'Notarial e Registral' },
  { area: 'civil', nome: 'Civil' },
  { area: 'processual', nome: 'Processual' },
  { area: 'publico', nome: 'Público' },
  { area: 'tributario', nome: 'Tributário' },
  { area: 'empresarial', nome: 'Empresarial' },
  { area: 'penal', nome: 'Penal' },
];

/**
 * O nome da estante, para quem tem só o `slug`.
 *
 * Lei sem estante não é erro: a estante só faz sentido depois que a lei tem
 * leitor, e o acervo é bem maior do que o catálogo. Elas se juntam no fim, sob
 * "Outras" - que é o que sai daqui quando não vem estante nenhuma.
 */
export function nomeDaArea(area: AreaDaLei | null | undefined): string {
  return AREAS_DA_LEI.find((estante) => estante.area === area)?.nome ?? 'Outras';
}

/**
 * A posição da estante na ordem da estante, para quem ordena por ela.
 *
 * Existe porque ordenar pelo **nome** é errado de um jeito discreto: alfabético,
 * "Outras" cai no meio da lista, entre "Notarial e Registral" e "Penal" - e a
 * pilha do que ninguém classificou é justamente o que se quer ver junto, no fim.
 */
export function ordemDaArea(area: AreaDaLei | null | undefined): number {
  const posicao = AREAS_DA_LEI.findIndex((estante) => estante.area === area);
  return posicao === -1 ? AREAS_DA_LEI.length : posicao;
}

/**
 * As estantes de uma lei, escritas para ler: `"Processual e Tributário"`.
 *
 * A lei sem estante nenhuma vira "Outras", que é onde o catálogo a põe - dizer
 * "-" aqui obrigaria quem lê a tabela a saber que os dois querem dizer o mesmo.
 */
export function nomeDasAreas(areas: readonly AreaDaLei[] | undefined): string {
  if (!areas || areas.length === 0) return nomeDaArea(null);
  const nomes = [...areas].sort((a, b) => ordemDaArea(a) - ordemDaArea(b)).map(nomeDaArea);
  if (nomes.length === 1) return nomes[0]!;
  return `${nomes.slice(0, -1).join(', ')} e ${nomes.at(-1)!}`;
}
/** Um dispositivo achado pela busca no texto das leis. */
export type AchadoNoTextoDaLei = JsonResponse<'getLawsSearch', 200>[number];
export type LeituraDaLei = JsonResponse<'getLawsId', 200>;
export type ArtigoParaLer = LeituraDaLei['itens'][number];
export type DivisaoDaLei = JsonResponse<'getLawsIdSumario', 200>[number];
/** O recorte da leitura: só os comentados, ou só os que a prova cobra. */
export type RecorteDaLeitura = 'comentados' | 'com-incidencia';
export type LeiDoAdmin = JsonResponse<'getAdminLaws', 200>[number];
export type LeiNoAdmin = JsonResponse<'getAdminLawsIdArticles', 200>;
export type ArtigoNoAdmin = LeiNoAdmin['itens'][number];
export type RecorteDaLei = NonNullable<NonNullable<JsonQuery<'getAdminLawsIdArticles'>>['recorte']>;
export type PaginaDeArtigos = JsonResponse<'getAdminCommentedArticles', 200>;
export type ArtigoComentado = PaginaDeArtigos['items'][number];
export type ResumoDosArtigos = JsonResponse<'getAdminCommentedArticlesSummary', 200>;
export type OrdemDosArtigos = NonNullable<
  NonNullable<JsonQuery<'getAdminCommentedArticles'>>['ordem']
>;
export type ArtigoComentadoDetalhe = JsonResponse<'getAdminCommentedArticlesId', 200>;
export type ArtigoPendente = JsonResponse<'getAdminCommentedArticlesBacklog', 200>[number];
/** O backlog conhece duas das ordens do inventário, e não as quatro. */
export type OrdemDoBacklog = NonNullable<
  NonNullable<JsonQuery<'getAdminCommentedArticlesBacklog'>>['ordem']
>;
/** De onde a lista sai: o que a prova cobra, ou a lei escolhida inteira. */
export type RecorteDoBacklog = NonNullable<
  NonNullable<JsonQuery<'getAdminCommentedArticlesBacklog'>>['recorte']
>;
export type EstadoDoArtigo = ArtigoComentado['state'];
export type PedidoDeArtigos = JsonResponse<'postAdminCommentedArticles', 200>;
export type CancelamentoDeArtigos = JsonResponse<'postAdminCommentedArticlesCancel', 200>;

export type ActivityPage = JsonResponse<'getActivity', 200>;
export type ActivityRow = ActivityPage['items'][number];

/** Painel de Progresso (F9): estoque, fila do quase pronto e ritmo diário. */
export type Panorama = JsonResponse<'getProgress', 200>;
export type ProgressoDeProva = Panorama['porProva'][number];
export type ItemDaFila = JsonResponse<'getProgressQueue', 200>[number];
export type DiaDeProgresso = JsonResponse<'getProgressDaily', 200>[number];
export type EstadoDoComentario = ItemDaFila['estado'];
/**
 * `type`, e não `interface`: só o alias ganha índice implícito e passa por
 * `Record<string, unknown>`, que é o que o montador de query string recebe.
 */
export type FiltrosDeProgresso = {
  scopeId?: string;
  examId?: string;
  module?: string;
  disciplineId?: string;
  responsibleId?: string;
};
export type TaxonomyScope = JsonResponse<'getScopes', 200>[number];
export type Disciplina = JsonResponse<'getDisciplines', 200>[number];

/** Árvore de assuntos com delimitações aninhadas, na ordem do administrador. */
export type SubjectTree = JsonResponse<'getDisciplinesIdTree', 200>;
export type SubjectTreeNode = SubjectTree[number];

export type Question = JsonResponse<'getQuestionsId', 200>;
export type QuestionResponsibles = JsonResponse<'getQuestionsResponsibles', 200>;
export type QuestionList = JsonResponse<'getQuestions', 200>;
export type QuestionSummary = QuestionList['items'][number];
export type QuestionInput = JsonBody<'postQuestions'>;
export type QuestionVersion = NonNullable<Question['latestVersion']>;
export type QuestionHistory = JsonResponse<'getQuestionsIdVersions', 200>;
export type QuestionStatus = Question['status'];
export type RichText = NonNullable<QuestionVersion['statement']>;
export type Commentary = NonNullable<QuestionVersion['commentary']>;
export type OptionLetter = NonNullable<QuestionVersion['correctOption']>;
/** Dissertação, peça prática ou questão discursiva: as partes da prova. */
export type EssayKind = NonNullable<QuestionVersion['essayKind']>;

/**
 * Os três arquivos de uma prova. O `edital` entrou com Provas para Imprimir.
 */
export type ExamFileKind = 'exam' | 'answer-key' | 'edital';

/* ------------------------------------------------ provas para imprimir */

export type ProvasPublicas = JsonResponse<'getPapers', 200>;
export type ProvaPublica = ProvasPublicas['items'][number];
export type FiltrosDeProvas = JsonResponse<'getPapersFilters', 200>;
export type EngajamentoDaProva = JsonResponse<'getExamsIdEngagement', 200>;
export type DiaDeUsoDaProva = EngajamentoDaProva['serie'][number];
export type UsosDaProva = JsonResponse<'getExamsIdEngagementPeople', 200>;
export type UsoDaProva = UsosDaProva['items'][number];
/** A prova como o backoffice a lista: com quantas vezes foi baixada. */
export type ExamDaLista = ExamList['items'][number];

/* ------------------------------------------------------- estudo do aluno */

export type StudyList = JsonResponse<'getStudyQuestions', 200>;
export type StudyQuestion = JsonResponse<'getStudyQuestionsId', 200>;
export type StudySummary = StudyList['items'][number];
export type StudyAnswer = JsonResponse<'postStudyQuestionsIdAnswer', 200>;
export type StudyCommentary = JsonResponse<'postStudyQuestionsIdCommentary', 200>;
export type StudyStatus = JsonResponse<'getStudyStatus', 200>;
/** Quanto o aluno já respondeu, quanto acertou e em que ritmo. */
export type StudyPerformance = JsonResponse<'getStudyPerformance', 200>;

/** O catálogo à venda, derivado do contrato. */
export type Product = JsonResponse<'getBillingProducts', 200>[number];
export type ProductPrice = Product['precos'][number];

/** Uma compra, na visão de quem pagou: é o que o recibo mostra. */
export type Purchase = JsonResponse<'getBillingOrders', 200>[number];

/** Uma recorrência da pessoa, separada do direito já pago. */
export type Subscription = JsonResponse<'getBillingSubscriptions', 200>[number];

/* -------------------------------------------------- o catálogo do Dono */

/**
 * O produto como quem administra o vê: preços, vendas e assinantes juntos.
 *
 * Nada aqui aparece para o aluno - o `Product` acima é o que a vitrine recebe,
 * e ele não carrega receita nem preço desativado.
 */
/**
 * As chaves de acesso que uma compra pode conceder.
 *
 * Derivadas do contrato, e não digitadas: chave nova no servidor vira erro de
 * tipo aqui, e não uma etiqueta em branco no seletor de `grants` (G-CONTRACT).
 */
export type ChaveDeAcesso = JsonBody<'postEntitlements'>['product'];

export type AdminProduct = JsonResponse<'getAdminProducts', 200>[number];
export type AdminPrice = AdminProduct['precos'][number];
export type AdminProductInput = JsonBody<'postAdminProducts'>;
/** `card` ou `pix`, derivado do contrato: forma nova na API vira erro de tipo aqui. */
export type FormaDePagamento = JsonBody<'postAdminProductsIdPrices'>['forma'];
export type AdminOrderPage = JsonResponse<'getAdminOrders', 200>;
export type AdminOrder = AdminOrderPage['items'][number];
export type AdminOrderDetail = JsonResponse<'getAdminOrdersId', 200>;
export type OrderStatus = AdminOrder['status'];
export type OrderCheck = JsonResponse<'postAdminOrdersIdReconcile', 200>;

/**
 * Notícias Recentes. Dois formatos, e o contrato os separa: o público não tem
 * `doc` nem `id` - o site não edita, e o que ele não recebe ele não expõe.
 */
export type NewsPage = JsonResponse<'getNews', 200>;
export type NewsListItem = NewsPage['items'][number];
export type NewsItem = JsonResponse<'getNewsSlug', 200>;
export type NewsAdminPage = JsonResponse<'getNewsDrafts', 200>;
export type NewsAdmin = JsonResponse<'getNewsIdId', 200>;
/** A figura guardada para um enunciado: o `url` é o que vai no `src` da imagem. */
export type QuestionImage = JsonResponse<'postQuestionImages', 200>;
export type NewsInput = JsonBody<'postNews'>;

/* A redação recebe ids e rascunhos; a leitura pública recebe só o que já saiu. */
export type MagazineIssueListItem = JsonResponse<'getMagazineIssues', 200>[number];
export type MagazineIssue = JsonResponse<'getMagazineIssuesSlug', 200>;
export type MagazineIssueAdminPage = JsonResponse<'getMagazineIssuesAll', 200>;
export type MagazineIssueAdmin = JsonResponse<'getMagazineIssuesIdId', 200>;
export type MagazineIssueInput = JsonBody<'postMagazineIssues'>;
export type MagazineArticlePage = JsonResponse<'getMagazineArticles', 200>;
export type MagazineArticleListItem = MagazineArticlePage['items'][number];
export type MagazineArticle = JsonResponse<'getMagazineArticlesSlug', 200>;
export type MagazineArticleAdminPage = JsonResponse<'getMagazineArticlesDrafts', 200>;
export type MagazineArticleAdmin = JsonResponse<'getMagazineArticlesIdId', 200>;
export type MagazineArticleInput = JsonBody<'postMagazineArticles'>;
export type MagazineSection = MagazineArticleInput['section'];
export type MagazineLicense = MagazineArticleAdmin['license'];
export type MagazineArticleFilters = Partial<JsonQuery<'getMagazineArticles'>>;
export type MagazineFilters = JsonResponse<'getMagazineFilters', 200>;
export type MagazineArticleEngagement = JsonResponse<'getMagazineArticlesIdEngagement', 200>;
export type MaterialListItem = JsonResponse<'getMaterials', 200>[number];
export type Material = JsonResponse<'getMaterialsSlug', 200>;
export type MaterialAdminPage = JsonResponse<'getMaterialsAll', 200>;
export type MaterialAdmin = JsonResponse<'getMaterialsIdId', 200>;
export type MaterialInput = JsonBody<'postMaterials'>;
export type MaterialLinks = MaterialAdmin['links'];
export type MaterialLinkDestination = MaterialLinks['chaves'][string];
export type MaterialEngagement = JsonResponse<'getMaterialsIdEngagement', 200>;
/** A origem de quem pede a entrega, que a API grava no evento da cópia. */
export type MaterialOrigin = Partial<JsonQuery<'getMaterialsSlugContent'>>;

/** O que o aluno comprou e até quando. */
export type StudyAccess = StudyStatus['acessos'][number];
export type AccessSettings = JsonResponse<'getAccessSettings', 200>;
/** O que a tela de Acesso pode ajustar. Parcial: manda só o que mudou. */
export type AjusteDeAcesso = NonNullable<
  NonNullable<operations['patchAccessSettings']['requestBody']>['content']['application/json']
>;
/**
 * As leis da Legislação Comentada como a página de vendas as mostra: nome,
 * citação oficial e quantos artigos já têm comentário. Sem o `acesso` do
 * catálogo do aluno, que é uma resposta sobre uma pessoa.
 */
export type LeiDaVitrine = JsonResponse<'getLawsCatalog', 200>[number];

/**
 * As duas cotas do visitante, abertas ao mundo.
 *
 * Subconjunto do `AccessSettings`, e não ele: o teto de quem paga e o limiar
 * de raspagem dizem onde está o alarme, e continuam atrás da guarda.
 */
export type LimitesDoGratuito = JsonResponse<'getAccessSettingsFree', 200>;

/** A lista da tela de Assinantes, derivada do contrato. */
export type SubscriberList = JsonResponse<'getEntitlements', 200>;
export type Subscriber = SubscriberList['items'][number];

/* --------------------------------------------------------------- pessoas */

/**
 * A seção de Pessoas: a lista, o funil e a pessoa por inteiro.
 *
 * O vocabulário fechado - a etapa da jornada, a situação de acesso, o degrau do
 * funil - vem do contrato e não de literais repetidos aqui: etapa nova no Zod
 * da rota vira erro de tipo na tela, e não uma etiqueta em branco (G-CONTRACT).
 */
export type PaginaDePessoas = JsonResponse<'getPeople', 200>;
export type PessoaNaLista = PaginaDePessoas['items'][number];
export type EtapaDaPessoa = PessoaNaLista['etapa'];
export type SituacaoDaPessoa = PessoaNaLista['situacao'];
/** Uma semana da faixa de recorrência; `dias` nulo é antes de a conta existir. */
export type SemanaDeEstudo = PessoaNaLista['semanas'][number];
/** Se a conta voltou a estudar: nunca, um dia só ou dois dias ou mais. */
export type RecorrenciaDaPessoa = NonNullable<
  NonNullable<operations['getPeople']['parameters']['query']>['recorrencia']
>;
/** Como a lista de pessoas pode ser ordenada. Ausente é a ordem de entrada. */
export type OrdemDasPessoas = NonNullable<
  NonNullable<operations['getPeople']['parameters']['query']>['ordem']
>;
export type ResumoDasPessoas = JsonResponse<'getPeopleSummary', 200>;
/** Quem volta a estudar: a base por semana de entrada. */
export type RecorrenciaDaBase = JsonResponse<'getPeopleRecurrence', 200>;
export type CoorteDeEntrada = RecorrenciaDaBase['coortes'][number];
export type DegrauDoFunil = ResumoDasPessoas['degraus'][number];
export type EtapaDoFunil = DegrauDoFunil['etapa'];
export type DetalheDaPessoa = JsonResponse<'getPeopleId', 200>;
/** Uma linha da lista de espera, que é aba de Pessoas desde 25/09/2026. */
export type LeadDaLista = JsonResponse<'getPeopleWaitlist', 200>['itens'][number];
export type GestoDeEstudo = DetalheDaPessoa['gestos'][number];
/** Por que a conta pode ler o comentario de uma lei: o vocabulario vem do contrato. */
export type LeiLidaPelaPessoa = DetalheDaPessoa['estudo']['leis'][number];
export type PorqueLeu = LeiLidaPelaPessoa['porque'];
export type PapelDaPessoa = JsonBody<'putPeopleIdRole'>['papel'];
export type ReportKind = JsonBody<'postReports'>['kind'];
export type ReportList = JsonResponse<'getReports', 200>;
export type Report = ReportList['items'][number];
export type ReportStatus = Report['status'];

/* ----------------------------------------------- cadastro assistido */

export type IngestionRun = JsonResponse<'getIngestionRunsId', 200>;
export type IngestionRunSummary = JsonResponse<'getExamsIdIngestionRuns', 200>[number];
export type IngestionStep = IngestionRun['steps'][number];
export type IngestionStepName = IngestionStep['step'];
export type IngestionRunList = JsonResponse<'getIngestionRuns', 200>;
export type IngestionRunRow = IngestionRunList['items'][number];
export type IngestionSettings = JsonResponse<'getIngestionSettings', 200>;
export type IngestionSettingsInput = JsonBody<'patchIngestionSettings'>;
/** As vozes da narração, vindas do gateway (D-21). */
export type IngestionModel = JsonResponse<'getIngestionSpeechModels', 200>[number];
export type IngestionDiscardPreview = JsonResponse<'getIngestionRunsIdDiscardPreview', 200>;

/** As citações do comentário, conferidas contra o acervo (PR-31). */
export type CitacoesConferidas = JsonResponse<'getQuestionsIdCitations', 200>;
export type CitacaoConferida = CitacoesConferidas['vereditos'][number];
export type SituacaoDaCitacao = CitacaoConferida['situacao'];

/** A lista de trabalho do Comentário Assistido (PR-31). */
export type PaginaDeComentarios = JsonResponse<'getCommentJobs', 200>;
export type ItemDaListaDeComentario = PaginaDeComentarios['items'][number];
export type EstadoDoItem = ItemDaListaDeComentario['status'];
export type MotorDoComentario = NonNullable<ItemDaListaDeComentario['engine']>;
/** O formato da fila: contagem por estado, travadas e as provas presentes. */
export type ResumoDaListaDeComentario = JsonResponse<'getCommentJobsSummary', 200>;

/**
 * O recorte da fila, o mesmo na lista e no resumo.
 *
 * `type` e não `interface` de propósito: só o alias ganha a assinatura de índice
 * implícita que o `comFiltros` exige, e é ela que permite nomear os filtros em
 * vez de passar um `Record<string, unknown>` - com o nome, a lista e a contagem
 * não têm como pedir recortes diferentes sem o compilador notar.
 */
export type FiltrosDeComentarios = {
  examId?: string;
  engine?: MotorDoComentario;
  /** Só o que está com um motor há tempo demais - a régua é do servidor. */
  paradas?: '1';
  termo?: string;
};

/* -------------------------------------------------- administradores */

/** O que a exclusão de uma conta apaga e o que preserva. */
export type UserDeletionImpact = JsonResponse<'getUsersIdDeletionImpact', 200>;

/** O cliente MCP que aparece na tela de consentimento. */
export type ClienteMcp = JsonResponse<'getMcpClientsClientId', 200>;

/**
 * Motivo da negativa que vem no `details` do 403 (G-POLICY).
 *
 * **São os motivos que o paywall sabe responder**, e por isso o `daily_cap`
 * ficou de fora: quem bate no teto diário já comprou, e oferecer-lhe a compra
 * seria o defeito de 03/09/2026 ao contrário. Ele tem porta própria, o
 * `ehTetoDiario` abaixo.
 *
 * **O `not_free_exam` ficou de fora daqui de 13/09 a 23/09/2026**, e a conta
 * gratuita que abria uma prova paga via um aviso de erro em vez da oferta: a
 * API negava com o motivo novo, e esta lista não o reconhecia. Motivo que a API
 * inventa e o app não conhece cai no `message.error`: motivo novo no
 * `MotivoNegado` da API pede a linha aqui e a frase no `SITUACAO` da oferta.
 */
export type MotivoDeBloqueio = 'free_limit' | 'not_entitled' | 'not_free_exam';

const MOTIVOS_DE_BLOQUEIO: readonly MotivoDeBloqueio[] = [
  'free_limit',
  'not_entitled',
  'not_free_exam',
];

const motivoDoErro = (erro: unknown): string | undefined =>
  erro instanceof ApiError && erro.status === 403
    ? (erro.details as { motivo?: string } | undefined)?.motivo
    : undefined;

/** Extrai o motivo do erro, que é o que decide a mensagem do paywall. */
export function motivoDoBloqueio(erro: unknown): MotivoDeBloqueio | null {
  const motivo = motivoDoErro(erro);
  return MOTIVOS_DE_BLOQUEIO.find((conhecido) => conhecido === motivo) ?? null;
}

/**
 * O 403 é do teto diário de quem assina (D-50)?
 *
 * Separado do `motivoDoBloqueio` de propósito: aqui não há o que vender, e
 * abrir a oferta seria dizer "assine" a quem já assinou. A resposta certa é um
 * aviso curto - a pessoa leu muito hoje, e amanhã o limite volta.
 */
export function ehTetoDiario(erro: unknown): boolean {
  return motivoDoErro(erro) === 'daily_cap';
}

/** O texto do aviso, num lugar só: as duas telas que o mostram dizem o mesmo. */
export const AVISO_DO_TETO_DIARIO =
  'Você abriu muitos comentários hoje. O limite volta à meia-noite.';

/**
 * A chave que abriria a porta, quando o 403 diz qual é.
 *
 * É o que faz a oferta vender **aquela** Legislação Comentada e não o primeiro
 * produto do catálogo: desde a produtização, cada lei tem chave própria (D-44),
 * e a tela não tem como adivinhar qual delas trancou.
 */
export function chaveDoBloqueio(erro: unknown): string | null {
  if (!(erro instanceof ApiError) || erro.status !== 403) return null;
  return (erro.details as { chave?: string } | undefined)?.chave ?? null;
}

/** Formato único de erro da API: `{ error: { code, message, details? } }`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Teto de espera por requisição. Existe para a interface nunca ficar presa. */
  timeoutMs?: number;
  /**
   * Cabeçalhos calculados a cada requisição.
   *
   * **Função, e não objeto**, porque o valor muda durante a vida da página: a
   * sessão do PostHog vira sozinha depois de um tempo parado, e um objeto
   * montado na criação do cliente carimbaria a sessão antiga até o recarregar.
   *
   * **Quem preenche é a aplicação, e não este pacote.** O `apps/app` sabe que
   * tem PostHog; o `apps/admin` não tem e não deve passar a ter só porque
   * compartilha o cliente. Importar `@titularis/analytics` daqui obrigaria os
   * três a carregar o SDK.
   *
   * Falha aqui não derruba a requisição: o que se perde é a correlação, e o que
   * se perderia ao propagar é a tela.
   */
  cabecalhos?: () => Record<string, string>;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * A espera de quem manda ou recebe um PDF inteiro.
 *
 * Os 15 s valem para JSON. Um PDF de 30 MB numa conexão de 10 Mbps leva perto
 * de 25 s só para subir, e o corte no meio é o pior dos casos: a tela diz que
 * falhou enquanto o servidor termina e troca o arquivo. Dois minutos cobrem a
 * rede lenta sem deixar uma aba pendurada para sempre.
 */
const ESPERA_DE_ARQUIVO_MS = 120_000;
const esperaDeArquivo = (): RequestInit => ({ signal: AbortSignal.timeout(ESPERA_DE_ARQUIVO_MS) });

/** Monta a query string ignorando filtros não preenchidos. */
function comFiltros(path: string, filtros: Record<string, unknown> = {}): string {
  const busca = new URLSearchParams();

  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '') continue;
    busca.set(chave, String(valor));
  }

  const query = busca.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * O nome do arquivo, lido do `content-disposition`.
 *
 * A forma da RFC 5987 vem primeiro porque é a que carrega acento: o servidor
 * manda as duas, e a simples é ASCII puro. Sem nome nenhum, "prova.pdf" - que
 * é melhor do que o navegador inventar um a partir do endereço da rota.
 */
function nomeDoCabecalho(cabecalho: string | null): string {
  if (!cabecalho) return 'prova.pdf';

  const codificado = /filename\*=UTF-8''([^;]+)/i.exec(cabecalho);
  if (codificado?.[1]) return decodeURIComponent(codificado[1]);

  const simples = /filename="([^"]+)"/i.exec(cabecalho);
  return simples?.[1] ?? 'prova.pdf';
}

export function createApiClient({
  baseUrl,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  cabecalhos,
}: ApiClientOptions) {
  const extras = (): Record<string, string> => {
    if (!cabecalhos) return {};
    try {
      return cabecalhos();
    } catch {
      return {};
    }
  };

  async function request<TBody>(path: string, init?: RequestInit): Promise<TBody> {
    let response: Response;

    // Upload de PDF vai como multipart: o navegador precisa definir o
    // content-type com o boundary, então não mandamos o nosso.
    const ehFormData = init?.body instanceof FormData;

    try {
      response = await fetch(`${baseUrl}${path}`, {
        // A sessão do BetterAuth é um cookie em .titularis.com.br, compartilhado
        // entre app e admin: toda requisição precisa carregá-lo.
        credentials: 'include',
        // Sem timeout, API fora do ar ou resposta bloqueada por CORS deixam a
        // promessa pendente e a tela travada em carregamento.
        signal: AbortSignal.timeout(timeoutMs),
        ...init,
        headers: ehFormData
          ? { ...extras(), ...init?.headers }
          : { 'content-type': 'application/json', ...extras(), ...init?.headers },
      });
    } catch (cause) {
      // Falha de rede, CORS ou timeout: o fetch rejeita sem resposta. Convertemos
      // para o erro da aplicação, para a interface ter o que mostrar.
      const isTimeout = cause instanceof DOMException && cause.name === 'TimeoutError';
      throw new ApiError(
        0,
        isTimeout ? 'timeout' : 'network',
        isTimeout
          ? 'A API demorou demais para responder.'
          : 'Não foi possível falar com a API. Verifique sua conexão.',
        cause,
      );
    }

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      const error = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)
        ?.error;
      throw new ApiError(
        response.status,
        error?.code ?? 'unknown',
        error?.message ?? 'Não foi possível concluir a operação.',
        error?.details,
      );
    }

    return payload as TBody;
  }

  const enviarJson = <TResposta>(path: string, method: string, body: unknown) =>
    request<TResposta>(path, { method, body: JSON.stringify(body) });

  /**
   * Um arquivo, e o nome com que o servidor mandou guardá-lo.
   *
   * O `request` acima sempre lê JSON, e PDF não é JSON: aqui a resposta vira
   * blob. O erro continua saindo pelo mesmo `ApiError`, porque a tela precisa
   * distinguir 401 - "crie uma conta gratuita" - de tudo o mais.
   */
  async function arquivo(
    path: string,
    espera = ESPERA_DE_ARQUIVO_MS,
  ): Promise<{ blob: Blob; nome: string }> {
    let response: Response;

    try {
      response = await fetch(`${baseUrl}${path}`, {
        credentials: 'include',
        headers: extras(),
        signal: AbortSignal.timeout(espera),
      });
    } catch (cause) {
      const isTimeout = cause instanceof DOMException && cause.name === 'TimeoutError';
      throw new ApiError(
        0,
        isTimeout ? 'timeout' : 'network',
        isTimeout
          ? 'A API demorou demais para responder.'
          : 'Não foi possível falar com a API. Verifique sua conexão.',
        cause,
      );
    }

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: { code?: string; message?: string };
      } | null;

      throw new ApiError(
        response.status,
        payload?.error?.code ?? 'unknown',
        payload?.error?.message ?? 'Não foi possível abrir o arquivo.',
        undefined,
      );
    }

    return {
      blob: await response.blob(),
      nome: nomeDoCabecalho(response.headers.get('content-disposition')),
    };
  }

  return {
    health: (): Promise<HealthResponse> => request<HealthResponse>('/health'),

    /* --------------------------------------------------------- catálogo */

    listBoards: (termo?: string) =>
      request<JsonResponse<'getBoards', 200>>(comFiltros('/boards', { termo, pageSize: 50 })),
    createBoard: (name: string) => enviarJson<ItemSimples>('/boards', 'POST', { name }),
    deleteBoard: (id: string) => request<{ ok: true }>(`/boards/${id}`, { method: 'DELETE' }),
    renameBoard: (id: string, name: string) => enviarJson<ItemSimples>(`/boards/${id}`, 'PATCH', { name }),

    listNotaryOffices: (termo?: string) =>
      request<JsonResponse<'getNotaryOffices', 200>>(
        comFiltros('/notary-offices', { termo, pageSize: 50 }),
      ),
    createNotaryOffice: (name: string, state?: string) =>
      enviarJson<ItemSimples>('/notary-offices', 'POST', { name, state }),
    deleteNotaryOffice: (id: string) =>
      request<{ ok: true }>(`/notary-offices/${id}`, { method: 'DELETE' }),
    renameNotaryOffice: (id: string, name: string) =>
      enviarJson<ItemSimples>(`/notary-offices/${id}`, 'PATCH', { name }),

    /**
     * Escopos. `kind: 'contest'` traz só os que aceitam prova - Legislações é
     * catálogo, e quem monta seletor de concurso não deve precisar saber disso
     * pelo slug.
     */
    listScopes: (filtros: { kind?: 'contest' | 'catalog' } = {}) =>
      request<TaxonomyScope[]>(comFiltros('/scopes', filtros)),
    /**
     * Disciplinas, opcionalmente de um escopo. A resposta traz o escopo sempre:
     * "Direito Civil" existe uma vez por concurso, e sem ele a lista repetiria
     * o mesmo rótulo (D-18).
     */
    listDisciplines: (filtros: { scopeId?: string; termo?: string } = {}) =>
      request<Disciplina[]>(comFiltros('/disciplines', filtros)),
    createDiscipline: (scopeId: string, name: string) =>
      enviarJson<ItemSimples>('/disciplines', 'POST', { scopeId, name }),
    deleteDiscipline: (id: string) =>
      request<{ ok: true }>(`/disciplines/${id}`, { method: 'DELETE' }),
    renameDiscipline: (id: string, name: string) =>
      enviarJson<ItemSimples>(`/disciplines/${id}`, 'PATCH', { name }),

    listSubjects: (disciplineId: string, termo?: string) =>
      request<ItemSimples[]>(comFiltros(`/disciplines/${disciplineId}/subjects`, { termo })),
    createSubject: (disciplineId: string, name: string) =>
      enviarJson<ItemSimples>(`/disciplines/${disciplineId}/subjects`, 'POST', { name }),
    deleteSubject: (id: string) => request<{ ok: true }>(`/subjects/${id}`, { method: 'DELETE' }),
    renameSubject: (id: string, name: string) =>
      enviarJson<ItemSimples>(`/subjects/${id}`, 'PATCH', { name }),
    reorderSubjects: (disciplineId: string, orderedIds: string[]) =>
      enviarJson<{ ok: true }>(`/disciplines/${disciplineId}/subjects/reorder`, 'POST', {
        orderedIds,
      }),

    listSubjectDetails: (subjectId: string, termo?: string) =>
      request<ItemSimples[]>(comFiltros(`/subjects/${subjectId}/details`, { termo })),
    createSubjectDetail: (subjectId: string, name: string) =>
      enviarJson<ItemSimples>(`/subjects/${subjectId}/details`, 'POST', { name }),
    deleteSubjectDetail: (id: string) =>
      request<{ ok: true }>(`/subject-details/${id}`, { method: 'DELETE' }),
    renameSubjectDetail: (id: string, name: string) =>
      enviarJson<ItemSimples>(`/subject-details/${id}`, 'PATCH', { name }),
    reorderSubjectDetails: (subjectId: string, orderedIds: string[]) =>
      enviarJson<{ ok: true }>(`/subjects/${subjectId}/details/reorder`, 'POST', { orderedIds }),

    /** Árvore de assuntos com as delimitações aninhadas, para o TreeSelect. */
    subjectTree: (disciplineId: string) =>
      request<SubjectTree>(`/disciplines/${disciplineId}/tree`),

    /* ------------------------------------------------------------ provas */

    listExams: (filtros: Record<string, unknown> = {}) =>
      request<ExamList>(comFiltros('/exams', filtros)),
    getExam: (id: string) => request<Exam>(`/exams/${id}`),
    createExam: (dados: ExamInput) => enviarJson<Exam>('/exams', 'POST', dados),
    updateExam: (id: string, dados: Partial<ExamInput>) =>
      enviarJson<Exam>(`/exams/${id}`, 'PATCH', dados),
    setExamStatus: (id: string, status: ExamStatus) =>
      enviarJson<Exam>(`/exams/${id}/status`, 'POST', { status }),
    deleteExam: (id: string) =>
      request<{ ok: true; questoes: number }>(`/exams/${id}`, { method: 'DELETE' }),

    uploadExamFile: (id: string, kind: ExamFileKind, arquivo: File) => {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      return request<Exam>(`/exams/${id}/files/${kind}`, { method: 'POST', body: corpo, ...esperaDeArquivo() });
    },
    examFileUrl: (id: string, kind: ExamFileKind) =>
      request<{ url: string }>(`/exams/${id}/files/${kind}`),

    /* ---------------------------------------------- provas para imprimir */

    /**
     * A listagem pública. Não exige sessão - é a única família de rotas do
     * catálogo que abre para quem ainda não tem conta.
     */
    listPapers: (filtros: Record<string, unknown> = {}) =>
      request<ProvasPublicas>(comFiltros('/papers', filtros)),
    paperFilters: () => request<FiltrosDeProvas>('/papers/filters'),
    getPaper: (slug: string) => request<ProvaPublica>(`/papers/${encodeURIComponent(slug)}`),

    /**
     * O PDF para ler na tela, como um blob do próprio navegador.
     *
     * **Blob, e não `<iframe src={url}>`.** O visualizador do navegador só
     * aceita `print()` de dentro quando o documento é da mesma origem, e o
     * arquivo vem de `api.titularis.com.br` enquanto a página está em
     * `titularis.com.br`. Um `blob:` criado aqui é da origem da página - é o
     * que faz o botão Imprimir imprimir, em vez de abrir uma aba e torcer.
     */
    paperFile: (slug: string, kind: ExamFileKind) =>
      arquivo(`/papers/${encodeURIComponent(slug)}/files/${kind}/content`),

    /**
     * O mesmo PDF, como endereço para abrir numa aba.
     *
     * **É o caminho do celular.** Chrome e Safari de telefone não desenham PDF
     * dentro de um `<iframe>`, então lá o leitor embutido não existe e o botão
     * abre o arquivo na aba do navegador, que sabe mostrá-lo. Baixar o blob
     * para um leitor que não vai aparecer seriam megabytes gastos no 4G de
     * alguém por nada.
     *
     * A sessão viaja mesmo assim: é navegação de primeiro nível para o mesmo
     * site registrável, e o cookie `Lax` acompanha - a abertura continua sendo
     * atribuída a quem já tem conta.
     */
    paperFileUrl: (slug: string, kind: ExamFileKind) =>
      `${baseUrl}/papers/${encodeURIComponent(slug)}/files/${kind}/content`,

    /**
     * Baixa o PDF. **Exige sessão** - é o único ponto do módulo que exige, e
     * quem não tem recebe 401 para a tela oferecer a conta gratuita.
     *
     * Passa pela API em vez de por um link direto para que o servidor decida o
     * acesso e conte o download no mesmo ato (G-POLICY).
     */
    downloadPaperFile: (slug: string, kind: ExamFileKind) =>
      arquivo(`/papers/${encodeURIComponent(slug)}/files/${kind}/download`),

    /** Anota que alguém mandou imprimir. Não devolve arquivo. */
    registerPaperPrint: (slug: string, kind: ExamFileKind) =>
      enviarJson<{ ok: true }>(
        `/papers/${encodeURIComponent(slug)}/files/${kind}/print`,
        'POST',
        {},
      ),

    /** Libera ou recolhe os arquivos da prova. Exige `question:publish`. */
    setExamFilesPublic: (id: string, filesPublic: boolean) =>
      enviarJson<Exam>(`/exams/${id}/files-public`, 'POST', { filesPublic }),

    examEngagement: (id: string, dias = 90) =>
      request<EngajamentoDaProva>(comFiltros(`/exams/${id}/engagement`, { dias })),
    examEngagementPeople: (id: string, filtros: Record<string, unknown> = {}) =>
      request<UsosDaProva>(comFiltros(`/exams/${id}/engagement/people`, filtros)),


    /* ---------------------------------------------------------- questões */

    listQuestions: (filtros: Record<string, unknown> = {}) =>
      request<QuestionList>(comFiltros('/questions', filtros)),
    /* ------------------------- Legislação Comentada por artigo (F14) */

    listLaws: () => request<LeiDoCatalogo[]>('/laws'),
    /**
     * Busca no **texto** das leis publicadas.
     *
     * Outra pergunta que a do catálogo: lá se procura a lei pelo nome, aqui se
     * procura o assunto dentro dela ("usucapião", "protesto por indicação").
     */
    searchLaws: (termo: string) =>
      request<AchadoNoTextoDaLei[]>(comFiltros('/laws/search', { q: termo })),
    /**
     * A lei numa página, ou **a página de um artigo**.
     *
     * Passando `artigo`, quem decide a página é o servidor, e a resposta diz
     * qual foi no campo `pagina` - é dela que a tela tira o `page=` da URL.
     */
    readLaw: (
      id: string,
      pagina: number,
      pageSize = 25,
      artigo?: string,
      recorte?: RecorteDaLeitura,
    ) =>
      request<LeituraDaLei>(
        comFiltros(`/laws/${id}`, { page: pagina, pageSize, artigo, recorte }),
      ),
    /** As divisões da lei, com a página em que cada uma começa. */
    lawIndex: (id: string, pageSize = 25) =>
      request<DivisaoDaLei[]>(comFiltros(`/laws/${id}/sumario`, { pageSize })),
    /**
     * Abre um comentário sozinho.
     *
     * É o caminho da degustação, e o único que consome cota: quem assina recebe
     * os comentários já embutidos na leitura da lei.
     */
    readArticleCommentary: (id: string, artigo: string) =>
      request<{ html: string }>(`/laws/${id}/articles/${encodeURIComponent(artigo)}/commentary`),

    listAdminLaws: () => request<LeiDoAdmin[]>('/admin/laws'),
    setLawPublication: (id: string, publicada: boolean) =>
      enviarJson<{ id: string; citation: string; publicada: boolean }>(
        `/admin/laws/${id}/publication`,
        'POST',
        { publicada },
      ),
    /**
     * Abre os comentários da lei para todo mundo, ou os fecha.
     *
     * Não é o mesmo que publicar: publicar põe o **texto** da lei no app, e o
     * texto é domínio público; isto abre o **comentário**, que é o que se vende.
     */
    setLawFree: (id: string, gratuita: boolean) =>
      enviarJson<{ id: string; gratuita: boolean }>(`/admin/laws/${id}/free`, 'POST', {
        gratuita,
      }),
    /**
     * Põe a lei nas prateleiras do catálogo.
     *
     * Manda o **estado final**, e não uma estante avulsa: a tela edita a
     * classificação inteira de uma vez. Lista vazia é valor legítimo, e não
     * omissão - é como se tira a lei de todas as prateleiras.
     */
    setLawAreas: (id: string, areas: AreaDaLei[]) =>
      enviarJson<{ id: string; citation: string; areas: AreaDaLei[] }>(
        `/admin/laws/${id}/areas`,
        'POST',
        { areas },
      ),
    /** A lei artigo a artigo, com o estado de cada um - inclusive o que ninguém cobra. */
    listLawArticles: (id: string, filtros: Record<string, unknown> = {}) =>
      request<LeiNoAdmin>(comFiltros(`/admin/laws/${id}/articles`, filtros)),
    /** O inventário: todo comentário que existe, em qualquer estado. */
    listCommentedArticles: (filtros: Record<string, unknown> = {}) =>
      request<PaginaDeArtigos>(comFiltros('/admin/commented-articles', filtros)),
    /** Quantos em cada estado, sob o mesmo recorte da lista. */
    commentedArticlesSummary: (filtros: Record<string, unknown> = {}) =>
      request<ResumoDosArtigos>(comFiltros('/admin/commented-articles/summary', filtros)),
    getCommentedArticle: (id: string) =>
      request<ArtigoComentadoDetalhe>(`/admin/commented-articles/${id}`),
    articleBacklog: (filtros: Record<string, unknown> = {}) =>
      request<ArtigoPendente[]>(comFiltros('/admin/commented-articles/backlog', filtros)),
    /**
     * Pede comentário de um lote de artigos, com uma ressalva para a leva.
     *
     * A resposta traz o que **não** entrou, com o motivo: no lote, artigo já
     * publicado ou já com um motor é recusa nomeada, e não erro da chamada.
     */
    requestArticleCommentary: (dados: {
      artigos: { legislationId: string; article: string }[];
      briefing?: string;
    }) => enviarJson<PedidoDeArtigos>('/admin/commented-articles', 'POST', dados),
    /** Desfaz o pedido. Nunca destrói comentário: o retrabalho volta a sugestão. */
    cancelArticleRequest: (artigos: { legislationId: string; article: string }[]) =>
      enviarJson<CancelamentoDeArtigos>('/admin/commented-articles/cancel', 'POST', { artigos }),
    updateArticleCommentary: (id: string, content: unknown) =>
      enviarJson<ArtigoComentado>(`/admin/commented-articles/${id}`, 'PATCH', { content }),
    setArticleCommentaryPublication: (id: string, publicado: boolean) =>
      enviarJson<ArtigoComentado>(`/admin/commented-articles/${id}/publication`, 'POST', {
        publicado,
      }),

    /* ------------------------------------------------------------ acervo */
    /*
     * Nenhum método de aluno aqui, e é estrutural (D-26): o acervo alimenta e
     * verifica a pipeline, e quem entrega material ao aluno é a Legislação
     * Comentada, por imagem marcada no servidor.
     */
    importCorpusLegislation: (dados: { citation: string; title: string; sourceUrl: string }) =>
      enviarJson<CorpusImportReport>('/corpus/legislations', 'POST', dados),
    listCorpusLegislations: () => request<FonteDeLegislacao[]>('/corpus/legislations'),
    reimportCorpusLegislation: (citation: string) =>
      enviarJson<RelatorioDeReimportacao>('/corpus/legislations/reimport', 'POST', { citation }),
    listCorpusDocuments: () => request<CorpusDocument[]>('/corpus/documents'),
    ingestCorpusDocument: (
      arquivo: File,
      dados: { title: string; source: string; series?: string; familia?: string },
    ) => {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      for (const [chave, valor] of Object.entries(dados)) {
        if (valor) corpo.append(chave, valor);
      }
      return request<CorpusIngestReport>('/corpus/documents', {
        method: 'POST',
        body: corpo,
        ...esperaDeArquivo(),
      });
    },
    setCorpusDocumentCurrency: (id: string, dados: { desatualizado: boolean; motivo?: string }) =>
      enviarJson<CorpusDocument>(`/corpus/documents/${id}/currency`, 'POST', dados),
    deleteCorpusDocument: (id: string) =>
      request<null>(`/corpus/documents/${id}`, { method: 'DELETE' }),
    searchCorpus: (filtros: Record<string, unknown>) =>
      request<CorpusHit[]>(comFiltros('/corpus/search', filtros)),

    /*
     * O backlog de ingestão (D-39): o que o Comentário Assistido procurou e não
     * achou, com quantas questões cada falta travou.
     */
    listCorpusGaps: () => request<LacunaDoAcervo[]>('/corpus/gaps'),
    resolveCorpusGap: (id: string, nota?: string) =>
      enviarJson<FechamentoDeLacuna>(`/corpus/gaps/${id}/resolve`, 'POST', { nota }),
    discardCorpusGap: (id: string, nota?: string) =>
      enviarJson<FechamentoDeLacuna>(`/corpus/gaps/${id}/discard`, 'POST', { nota }),

    /* ---------------------------------------------- trilha de auditoria */
    listActivity: (filtros: Record<string, unknown> = {}) =>
      request<ActivityPage>(comFiltros('/activity', filtros)),
    listActivityActors: () =>
      request<{ id: string; name: string | null }[]>('/activity/actors'),
    listActivityActions: () => request<string[]>('/activity/actions'),

    /* ------------------------------------------- progresso dos comentários */
    /**
     * O estoque numa chamada só: resumo, por prova e entregas do catálogo. Vão
     * juntos porque a tela abre com os três, e separá-los faria a primeira
     * pintura esperar três idas e voltas em fila.
     */
    progresso: (filtros: FiltrosDeProgresso = {}) =>
      request<Panorama>(comFiltros('/progress', filtros)),
    filaDoProgresso: (filtros: FiltrosDeProgresso & { limite?: number } = {}) =>
      request<ItemDaFila[]>(comFiltros('/progress/queue', filtros)),
    /**
     * `bloco` é o tamanho da coluna, em horas, e precisa dividir 24 - a rota
     * recusa o resto com 422. Ausente, o bloco é o dia.
     */
    ritmoDoProgresso: (filtros: FiltrosDeProgresso & { dias?: number; bloco?: number } = {}) =>
      request<DiaDeProgresso[]>(comFiltros('/progress/daily', filtros)),

    listQuestionResponsibles: () => request<QuestionResponsibles>('/questions/responsibles'),
    getQuestion: (id: string) => request<Question>(`/questions/${id}`),
    listQuestionVersions: (id: string) => request<QuestionHistory>(`/questions/${id}/versions`),
    createQuestion: (dados: QuestionInput) => enviarJson<Question>('/questions', 'POST', dados),
    updateQuestion: (id: string, dados: Omit<QuestionInput, 'examId'>) =>
      enviarJson<Question>(`/questions/${id}`, 'PATCH', dados),
    uploadQuestionImage: (arquivo: File) => {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      return request<QuestionImage>('/question-images', { method: 'POST', body: corpo });
    },
    publishQuestion: (id: string, versionId?: string) =>
      enviarJson<Question>(`/questions/${id}/publish`, 'POST', { versionId }),
    annulQuestion: (id: string) => enviarJson<Question>(`/questions/${id}/annul`, 'POST', {}),
    unannulQuestion: (id: string) => enviarJson<Question>(`/questions/${id}/unannul`, 'POST', {}),
    setQuestionFree: (id: string, isFree: boolean) =>
      enviarJson<Question>(`/questions/${id}/free`, 'POST', { isFree }),
    setQuestionsFree: (questionIds: string[], isFree: boolean) =>
      enviarJson<{ alteradas: number }>('/questions/free-samples', 'POST', { questionIds, isFree }),
    unpublishQuestion: (id: string) =>
      enviarJson<Question>(`/questions/${id}/unpublish`, 'POST', {}),
    unpublishQuestions: (questionIds: string[]) =>
      enviarJson<{ despublicadas: number; ignoradas: number }>(
        '/questions/unpublish-batch',
        'POST',
        { questionIds },
      ),
    publishQuestions: (questionIds: string[]) =>
      enviarJson<{ publicadas: number; ignoradas: number }>('/questions/publish-batch', 'POST', {
        questionIds,
      }),
    deleteQuestion: (id: string) => request<{ ok: true }>(`/questions/${id}`, { method: 'DELETE' }),

    /* ------------------------------------------------- estudo do aluno */

    listStudyQuestions: (filtros: Record<string, unknown> = {}) =>
      request<StudyList>(comFiltros('/study/questions', filtros)),
    getStudyQuestion: (id: string) => request<StudyQuestion>(`/study/questions/${id}`),
    answerStudyQuestion: (id: string, selectedOption: string) =>
      enviarJson<StudyAnswer>(`/study/questions/${id}/answer`, 'POST', { selectedOption }),
    undoStudyAnswer: (id: string) =>
      request<{ ok: true }>(`/study/questions/${id}/answer`, { method: 'DELETE' }),
    revealCommentary: (id: string) =>
      enviarJson<StudyCommentary>(`/study/questions/${id}/commentary`, 'POST', {}),
    studyStatus: () => request<StudyStatus>('/study/status'),
    studyPerformance: () => request<StudyPerformance>('/study/performance'),

    /*
     * Compra. O front não conhece o provedor: pede um checkout e recebe uma
     * `url` para onde mandar o comprador. Trocar de gateway não toca aqui.
     */
    listProducts: () => request<Product[]>('/billing/products'),
    /**
     * As compras de quem está logado, com valor, forma e número do pedido.
     *
     * Quem é o dono da lista não viaja no pedido: é a sessão que decide, no
     * servidor. Não há como perguntar pelo recibo de outra pessoa.
     */
    minhasCompras: () => request<Purchase[]>('/billing/orders'),
    /** As recorrências da conta; cancelada continua no histórico. */
    minhasAssinaturas: () => request<Subscription[]>('/billing/subscriptions'),
    /** Para a cobrança seguinte sem revogar o ciclo que já foi pago. */
    cancelarAssinatura: (id: string) =>
      enviarJson<JsonResponse<'postBillingSubscriptionsIdCancel', 200>>(
        `/billing/subscriptions/${id}/cancel`,
        'POST',
        {},
      ),
    /*
     * Pública como a tabela de preços, e pelo mesmo motivo: a página de vendas
     * precisa dizer o que a conta grátis dá, e o número muda na tela de Acesso
     * sem passar por deploy.
     */
    limitesDoGratuito: () => request<LimitesDoGratuito>('/access-settings/free'),
    /* Pública como o catálogo de preços: quem ainda não comprou precisa conferir o que abre. */
    catalogoDeLeis: () => request<LeiDaVitrine[]>('/laws/catalog'),

    /* ------------------------------------------------ a lista de e-mails */

    /**
     * Entra numa lista: novidades, ou a fila de um módulo que ainda não saiu.
     *
     * **Não exige sessão**, e é o ponto: quem entra na fila da 2ª fase em geral
     * não tem conta, e pedir cadastro antes derrubaria a maior parte das
     * inscrições. Se houver sessão, a API preenche o vínculo sozinha.
     *
     * Responde igual para e-mail novo e repetido - a rota é pública e não pode
     * virar verificador de endereços alheios.
     */
    entrarNaLista: (dados: {
      email: string;
      topic: TopicoDeLista;
      source: OrigemDeLista;
      atribuicao?: Origem | null;
    }) => enviarJson<{ ok: true }>('/leads', 'POST', dados),

    /** Sai da lista pelo link da mensagem. Sem login, de propósito. */
    sairDaLista: (token: string) =>
      enviarJson<{ ok: true }>('/leads/unsubscribe', 'POST', { token }),

    /**
     * O funil por canal: contas, inscrições e receita de cada origem.
     *
     * Vem do nosso banco, e não do PostHog: é o número do dinheiro, e ele não
     * pode depender de o navegador ter conseguido mandar o evento.
     */
    funilPorOrigem: () => request<LinhaDoFunil[]>('/attribution/funnel'),

    /* ------------------------------------------------ Notícias Recentes */

    /*
     * As duas primeiras são chamadas pelo site, sem sessão. As demais são do
     * admin. A separação existe na API (`/news` só enxerga o que está no ar,
     * `/news/drafts` enxerga tudo), e não num parâmetro daqui: filtro que o
     * cliente manda é filtro que o cliente pode esquecer de mandar.
     */
    listNews: (page = 1, pageSize = 10) =>
      request<NewsPage>(comFiltros('/news', { page, pageSize })),
    getNews: (slug: string) => request<NewsItem>(`/news/${encodeURIComponent(slug)}`),

    /**
     * O endereço da capa, para `<img src>`.
     *
     * Sai de uma rota nossa, e não de uma URL do bucket, que é privado (D-5):
     * signed URL resolveria a leitura mas expira em minutos, e colada num
     * `<img>` viraria imagem quebrada no primeiro compartilhamento que
     * demorasse a ser aberto.
     *
     * Mora aqui, e não em cada app, porque a capa passou a aparecer em dois
     * lugares - a listagem do site e a tela de início do aluno. A mesma linha
     * escrita duas vezes é a que fica para trás quando a rota mudar.
     *
     * **É `/image`, e não `/card`.** A rota do cartão remonta a capa em
     * 1200x630 com fundo desfocado nas sobras, e existe para o robô de prévia
     * do WhatsApp - dentro de uma miniatura de 84px aquelas faixas viram uma
     * borda cinza em volta da imagem de verdade.
     */
    newsImageUrl: (slug: string) => `${baseUrl}/news/${encodeURIComponent(slug)}/image`,

    listNewsDrafts: (page = 1, pageSize = 50) =>
      request<NewsAdminPage>(comFiltros('/news/drafts', { page, pageSize })),
    getNewsById: (id: string) => request<NewsAdmin>(`/news/id/${id}`),
    createNews: (dados: NewsInput) => enviarJson<NewsAdmin>('/news', 'POST', dados),
    updateNews: (id: string, dados: NewsInput) =>
      enviarJson<NewsAdmin>(`/news/${id}`, 'PATCH', dados),
    deleteNews: (id: string) => request<{ ok: true }>(`/news/${id}`, { method: 'DELETE' }),
    uploadNewsImage: (id: string, arquivo: File) => {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      return request<NewsAdmin>(`/news/${id}/image`, { method: 'POST', body: corpo });
    },
    /* ----------------------------------------------------------- revista */

    listMagazineIssues: () => request<MagazineIssueListItem[]>('/magazine/issues'),
    getMagazineIssue: (slug: string) =>
      request<MagazineIssue>(`/magazine/issues/${encodeURIComponent(slug)}`),
    listMagazineArticles: (filters: MagazineArticleFilters = {}) =>
      request<MagazineArticlePage>(comFiltros('/magazine/articles', filters)),
    getMagazineArticle: (slug: string) =>
      request<MagazineArticle>(`/magazine/articles/${encodeURIComponent(slug)}`),
    magazineFilters: () => request<MagazineFilters>('/magazine/filters'),
    magazineArticleCoverUrl: (slug: string) =>
      `${baseUrl}/magazine/articles/${encodeURIComponent(slug)}/cover`,
    magazineIssueCoverUrl: (slug: string) =>
      `${baseUrl}/magazine/issues/${encodeURIComponent(slug)}/cover`,
    magazineArticleFileUrl: (slug: string) =>
      `${baseUrl}/magazine/articles/${encodeURIComponent(slug)}/content`,
    // O leitor recebe o mesmo par blob/nome das provas, inclusive nos erros de sessão.
    magazineArticleFile: (slug: string) =>
      arquivo(`/magazine/articles/${encodeURIComponent(slug)}/content`),
    downloadMagazineArticle: (slug: string) =>
      arquivo(`/magazine/articles/${encodeURIComponent(slug)}/download`),
    registerMagazineArticlePrint: (slug: string) =>
      enviarJson<{ ok: true }>(`/magazine/articles/${encodeURIComponent(slug)}/print`, 'POST', {}),

    listMagazineIssuesAll: (page = 1, pageSize = 50) =>
      request<MagazineIssueAdminPage>(comFiltros('/magazine/issues/all', {
        page,
        pageSize,
      })),
    createMagazineIssue: (data: MagazineIssueInput) =>
      enviarJson<MagazineIssueAdmin>('/magazine/issues', 'POST', data),
    updateMagazineIssue: (id: string, data: MagazineIssueInput) =>
      enviarJson<MagazineIssueAdmin>(`/magazine/issues/${id}`, 'PATCH', data),
    deleteMagazineIssue: (id: string) =>
      request<{ ok: true }>(`/magazine/issues/${id}`, { method: 'DELETE' }),
    uploadMagazineIssueCover: (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return request<MagazineIssueAdmin>(`/magazine/issues/${id}/cover`, {
        method: 'POST',
        body,
      });
    },
    listMagazineArticleDrafts: (page = 1, pageSize = 50) =>
      request<MagazineArticleAdminPage>(comFiltros('/magazine/articles/drafts', {
        page,
        pageSize,
      })),
    getMagazineArticleById: (id: string) =>
      request<MagazineArticleAdmin>(`/magazine/articles/id/${id}`),
    createMagazineArticle: (data: MagazineArticleInput) =>
      enviarJson<MagazineArticleAdmin>('/magazine/articles', 'POST', data),
    updateMagazineArticle: (id: string, data: MagazineArticleInput) =>
      enviarJson<MagazineArticleAdmin>(`/magazine/articles/${id}`, 'PATCH', data),
    deleteMagazineArticle: (id: string) =>
      request<{ ok: true }>(`/magazine/articles/${id}`, { method: 'DELETE' }),
    uploadMagazineArticleCover: (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return request<MagazineArticleAdmin>(`/magazine/articles/${id}/cover`, {
        method: 'POST',
        body,
      });
    },
    uploadMagazineArticlePdf: (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return request<MagazineArticleAdmin>(`/magazine/articles/${id}/pdf`, {
        method: 'POST',
        body,
        ...esperaDeArquivo(),
      });
    },
    magazineArticleEngagement: (id: string, days = 30) =>
      request<MagazineArticleEngagement>(
        comFiltros(`/magazine/articles/${id}/engagement`, { dias: days }),
      ),

    /* --------------------------------------------------- materiais grátis */

    listMaterials: () => request<MaterialListItem[]>('/materials'),
    getMaterial: (slug: string) =>
      request<Material>(`/materials/${encodeURIComponent(slug)}`),
    materialCoverUrl: (slug: string) =>
      `${baseUrl}/materials/${encodeURIComponent(slug)}/cover`,
    /**
     * O endereço da leitura, com a origem da visita na query.
     *
     * No celular o leitor abre este endereço numa aba nova, sem `fetch`: é por
     * isso que a origem viaja na URL, e não num corpo.
     */
    materialFileUrl: (slug: string, origem: MaterialOrigin = {}) =>
      `${baseUrl}${comFiltros(`/materials/${encodeURIComponent(slug)}/content`, origem)}`,
    materialFile: (slug: string, origem: MaterialOrigin = {}) =>
      arquivo(comFiltros(`/materials/${encodeURIComponent(slug)}/content`, origem)),
    downloadMaterial: (slug: string, origem: MaterialOrigin = {}) =>
      arquivo(comFiltros(`/materials/${encodeURIComponent(slug)}/download`, origem)),
    registerMaterialPrint: (slug: string) =>
      enviarJson<{ ok: true }>(`/materials/${encodeURIComponent(slug)}/print`, 'POST', {}),

    listMaterialsAll: (page = 1, pageSize = 50) =>
      request<MaterialAdminPage>(comFiltros('/materials/all', { page, pageSize })),
    getMaterialById: (id: string) => request<MaterialAdmin>(`/materials/id/${id}`),
    createMaterial: (data: MaterialInput) =>
      enviarJson<MaterialAdmin>('/materials', 'POST', data),
    updateMaterial: (id: string, data: MaterialInput) =>
      enviarJson<MaterialAdmin>(`/materials/${id}`, 'PATCH', data),
    deleteMaterial: (id: string) =>
      request<{ ok: true }>(`/materials/${id}`, { method: 'DELETE' }),
    uploadMaterialCover: (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return request<MaterialAdmin>(`/materials/${id}/cover`, { method: 'POST', body });
    },
    uploadMaterialPdf: (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return request<MaterialAdmin>(`/materials/${id}/pdf`, { method: 'POST', body, ...esperaDeArquivo() });
    },
    /** A cópia de um grupo: o PDF marcado, com o rótulo que o engajamento vai mostrar. */
    materialGroupCopy: (id: string, rotulo: string) =>
      arquivo(comFiltros(`/materials/${id}/copy`, { rotulo })),
    materialEngagement: (id: string, days = 30) =>
      request<MaterialEngagement>(comFiltros(`/materials/${id}/engagement`, { dias: days })),

    /**
     * A `atribuicao` viaja junto com a compra, e não numa chamada à parte.
     *
     * É o momento em que ela precisa existir: o pedido nasce aqui e congela o
     * canal junto com o valor. Mandá-la depois abriria a janela em que uma
     * venda existe sem origem - e é justamente a venda que a pergunta "esse
     * anúncio deu retorno?" precisa achar.
     */
    createCheckout: (dados: {
      produto: string;
      priceId: string;
      atribuicao?: Origem | null;
    }) => enviarJson<{ orderId: string; url: string }>('/billing/checkout', 'POST', dados),

    /** Grava o canal de chegada na conta. Só vale a primeira vez; ver a rota. */
    registrarAtribuicao: (atribuicao: Origem | null) =>
      enviarJson<{ ok: true }>('/me/attribution', 'POST', { attribution: atribuicao }),

    /*
     * O catálogo por dentro. Só o Dono - as rotas exigem `billing:manage`.
     *
     * Toda escrita devolve **o produto inteiro**, e não só o que mudou: mexer
     * num preço mexe no irmão da mesma forma (a API garante um ativo por forma),
     * e devolver a parte faria a tela mostrar um estado que não existe no banco.
     */
    listAdminProducts: () => request<AdminProduct[]>('/admin/products'),
    getAdminProduct: (id: string) => request<AdminProduct>(`/admin/products/${id}`),
    createProduct: (dados: AdminProductInput) =>
      enviarJson<AdminProduct>('/admin/products', 'POST', dados),
    updateProduct: (id: string, dados: Partial<Omit<AdminProductInput, 'key'>>) =>
      enviarJson<AdminProduct>(`/admin/products/${id}`, 'PATCH', dados),
    deleteProduct: (id: string) =>
      request<void>(`/admin/products/${id}`, { method: 'DELETE' }),
    setPrice: (
      productId: string,
      dados: {
        forma: FormaDePagamento;
        /** Meses de acesso que cada pagamento concede. Um é mensal; doze, anual. */
        accessMonths: number;
        /** Assinatura em vez de cobrança avulsa. Só o cartão aceita. */
        recurring: boolean;
        amountCents: number;
        maxInstallments: number;
      },
    ) => enviarJson<AdminProduct>(`/admin/products/${productId}/prices`, 'POST', dados),
    togglePrice: (priceId: string, active: boolean) =>
      enviarJson<AdminProduct>(`/admin/prices/${priceId}`, 'PATCH', { active }),
    deletePrice: (priceId: string) =>
      request<AdminProduct>(`/admin/prices/${priceId}`, { method: 'DELETE' }),

    listOrders: (filtros: Record<string, unknown> = {}) =>
      request<AdminOrderPage>(comFiltros('/admin/orders', filtros)),
    getOrder: (id: string) => request<AdminOrderDetail>(`/admin/orders/${id}`),
    /** Pergunta ao provedor o que ele sabe do pedido e aplica o que vier. */
    checkOrder: (id: string) => enviarJson<OrderCheck>(`/admin/orders/${id}/reconcile`, 'POST', {}),

    /*
     * Assinantes: quem tem acesso pago, e as duas ações que consertam uma
     * compra que deu errado. Só o Dono - as rotas recusam os demais.
     */
    listSubscribers: (filtros: Record<string, unknown> = {}) =>
      request<SubscriberList>(comFiltros('/entitlements', filtros)),
    grantEntitlement: (dados: {
      userId: string;
      product: string;
      /** Zero significa sem prazo. */
      meses: number;
      motivo?: string;
    }) => enviarJson<unknown>('/entitlements', 'POST', dados),
    /*
     * A chave também é escapada, e passou a precisar disso: desde a
     * produtização ela pode ser `commented_law:<uuid>`, e chave crua no caminho
     * depende de o `:` sobreviver a tudo o que houver entre o navegador e o
     * Express. Escapada, é um segmento como outro qualquer.
     */
    revokeEntitlement: (userId: string, product: string) =>
      request<void>(`/entitlements/${encodeURIComponent(userId)}/${encodeURIComponent(product)}`, {
        method: 'DELETE',
      }),

    /* ------------------------------------------------------------ pessoas */

    /**
     * A lista de pessoas, a única do backoffice que junta identidade, uso e
     * compra na mesma linha. Substitui a chamada direta ao
     * `authClient.admin.listUsers`, que trazia só a identidade.
     */
    listPeople: (filtros: Record<string, unknown> = {}) =>
      request<PaginaDePessoas>(comFiltros('/people', filtros)),
    /** O funil: quantos chegaram a cada etapa. */
    peopleSummary: () => request<ResumoDasPessoas>('/people/summary'),
    /** Quem volta a estudar: por semana de entrada, e nas duas últimas semanas. */
    peopleRecurrence: () => request<RecorrenciaDaBase>('/people/recurrence'),
    /**
     * A lista de espera inteira, com quem saiu, para conferir e exportar. Era
     * `/leads` até virar aba de Pessoas; `semInternas` segue a régua do funil.
     */
    listaDeEspera: (
      filtros: { topic?: string; semInternas?: boolean; page?: number; pageSize?: number } = {},
    ) => request<{ itens: LeadDaLista[]; total: number }>(comFiltros('/people/waitlist', filtros)),
    /** Quantos esperam cada coisa: é o número que decide o próximo módulo. */
    resumoDaListaDeEspera: (filtros: { semInternas?: boolean } = {}) =>
      request<JsonResponse<'getPeopleWaitlistSummary', 200>>(
        comFiltros('/people/waitlist/summary', filtros),
      ),
    /** Tudo o que a casa sabe sobre uma conta, numa chamada. */
    getPerson: (id: string) => request<DetalheDaPessoa>(`/people/${encodeURIComponent(id)}`),
    /** Manda de novo o e-mail de confirmação, para o endereço já cadastrado. */
    resendVerification: (id: string) =>
      enviarJson<{ ok: true; email: string }>(
        `/people/${encodeURIComponent(id)}/verification-email`,
        'POST',
        {},
      ),
    suspendPerson: (id: string, motivo: string) =>
      enviarJson<{ ok: true }>(`/people/${encodeURIComponent(id)}/suspension`, 'POST', { motivo }),
    unsuspendPerson: (id: string) =>
      request<{ ok: true }>(`/people/${encodeURIComponent(id)}/suspension`, { method: 'DELETE' }),
    revokePersonSessions: (id: string) =>
      enviarJson<{ ok: true; encerradas: number }>(
        `/people/${encodeURIComponent(id)}/sessions/revoke`,
        'POST',
        {},
      ),
    setPersonRole: (id: string, papel: PapelDaPessoa) =>
      enviarJson<{ ok: true }>(`/people/${encodeURIComponent(id)}/role`, 'PUT', { papel }),
    /** Marca ou desmarca a conta como de teste, o que a tira do funil e das métricas. */
    setPersonTestAccount: (id: string, teste: boolean) =>
      enviarJson<{ ok: true }>(`/people/${encodeURIComponent(id)}/test-account`, 'PUT', { teste }),

    getAccessSettings: () => request<AccessSettings>('/access-settings'),
    /**
     * O corpo vem do contrato, e não de uma lista escrita à mão.
     *
     * Era escrita à mão e ficou para trás na primeira vez que a tela de Acesso
     * ganhou um campo - `freeExamId`, em 13/09/2026. Derivado, campo novo no
     * Zod da rota chega aqui com o `pnpm api:types`, sem ninguém lembrar.
     */
    updateAccessSettings: (dados: AjusteDeAcesso) =>
      enviarJson<AccessSettings>('/access-settings', 'PATCH', dados),

    createReport: (dados: { questionId: string; kind: ReportKind; message?: string | null }) =>
      enviarJson<{ id: string }>('/reports', 'POST', dados),
    listReports: (filtros: Record<string, unknown> = {}) =>
      request<ReportList>(comFiltros('/reports', filtros)),
    resolveReport: (id: string, status: 'resolved' | 'dismissed', resolution?: string | null) =>
      enviarJson<{ id: string }>(`/reports/${id}/resolve`, 'POST', { status, resolution }),

    /*
     * Cadastro Assistido: só leitura e descarte.
     *
     * Quem aciona um cadastro é o `abrir_prova` do MCP, na máquina de quem opera
     * o Claude Code. O que a tela faz é acompanhar e desfazer.
     */

    getIngestionRun: (id: string) => request<IngestionRun>(`/ingestion/runs/${id}`),
    listIngestionRuns: (examId: string) =>
      request<IngestionRunSummary[]>(`/exams/${examId}/ingestion-runs`),

    listAllIngestionRuns: (filtros: Record<string, unknown> = {}) =>
      request<IngestionRunList>(comFiltros('/ingestion/runs', filtros)),

    /* As citações do comentário, conferidas contra o acervo. */
    conferirCitacoes: (questionId: string) =>
      request<CitacoesConferidas>(`/questions/${questionId}/citations`),

    /*
     * A lista de trabalho do Comentário Assistido, paginada.
     *
     * O resumo vem à parte porque não muda ao virar a página nem ao trocar de
     * estado: pedi-lo junto faria o banco recontar a fila inteira a cada clique,
     * inclusive nas atualizações automáticas enquanto há motor trabalhando.
     */
    listarComentarios: (
      filtros: FiltrosDeComentarios & {
        status?: EstadoDoItem;
        ordem?: 'recentes' | 'antigas';
        page?: number;
        pageSize?: number;
      } = {},
    ) => request<PaginaDeComentarios>(comFiltros('/comment-jobs', filtros)),

    resumoDosComentarios: (filtros: FiltrosDeComentarios = {}) =>
      request<ResumoDaListaDeComentario>(comFiltros('/comment-jobs/summary', filtros)),
    /*
     * O `briefing` e a ressalva de quem revisou, e vale para o lote inteiro
     * (D-41): quem seleciona questoes e manda recomentar esta dizendo a mesma
     * coisa sobre todas elas.
     */
    enfileirarComentarios: (
      questionIds: string[],
      requestedFields: string[],
      briefing?: string,
    ) =>
      enviarJson<{ enfileiradas: number; jaNaLista: string[]; ressalvaNaoAlcancou: number }>(
        '/comment-jobs',
        'POST',
        {
          questionIds,
          requestedFields,
          ...(briefing?.trim() ? { briefing: briefing.trim() } : {}),
        },
      ),
    devolverComentario: (id: string) =>
      enviarJson<{ ok: true }>(`/comment-jobs/${id}/return`, 'POST', {}),
    desenfileirarComentario: (id: string) =>
      request<{ ok: true }>(`/comment-jobs/${id}`, { method: 'DELETE' }),
    getIngestionSettings: () => request<IngestionSettings>('/ingestion/settings'),
    listSpeechModels: () => request<IngestionModel[]>('/ingestion/speech-models'),

    previewDiscardRun: (runId: string) =>
      request<IngestionDiscardPreview>(`/ingestion/runs/${runId}/discard-preview`),
    discardRunSuggestions: (runId: string) =>
      enviarJson<{ apagadas: number; preservadas: number }>(
        `/ingestion/runs/${runId}/discard`,
        'POST',
        {},
      ),
    updateIngestionSettings: (dados: IngestionSettingsInput) =>
      enviarJson<IngestionSettings>('/ingestion/settings', 'PATCH', dados),
    /* ----------------------------------------------- administradores */

    /**
     * Quem é o cliente MCP que pede autorização.
     *
     * A tela de consentimento mostrava só o `client_id` opaco, e o registro de
     * cliente é aberto por desenho (D-52): sem nome e sem endereço de retorno,
     * não há como distinguir o nosso cliente de um forjado.
     */
    mcpClient: (clientId: string) =>
      request<ClienteMcp>(`/mcp/clients/${encodeURIComponent(clientId)}`),

    userDeletionImpact: (userId: string) =>
      request<UserDeletionImpact>(`/users/${userId}/deletion-impact`),
    deleteUser: (userId: string) =>
      request<{ ok: true }>(`/users/${userId}`, { method: 'DELETE' }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** Opções prontas para o TanStack Query. */
export const healthQuery = (api: ApiClient) => ({
  queryKey: ['health'] as const,
  queryFn: () => api.health(),
});

export {
  createAuth,
  ROLES,
  ROLE_LABEL,
  ROLE_DESCRIPTION,
  ADMIN_ROLES,
  isAdminRole,
  FOCUS_MODULES,
  focosValidos,
} from './auth';
export type { AuthClient, Role, FocusModule } from './auth';
